/** Static data extracted from Main.dc.html — post groups, roster, P-order, CCI seats.
 *  These are the exact values the prototype uses. */

export interface PostDef {
  name: string;
  shortName: string;
  held: boolean;   // true = held all shift, false = rotates
}

export interface GroupDef {
  name: string;
  note: string;
  color: string;
  posts: PostDef[];
}

/** Post groups — matches the G array in Main.dc.html lines 1150–1156.
 *  Index 5 = Report Writers, index 6 = Dispatch (virtual, not in G). */
export const GROUPS: GroupDef[] = [
  { name: 'Main', note: 'Gaming, corridors, Hotel 1 · held all shift', color: '#2563EB', posts: [
    { name: 'Gaming 1', shortName: 'Sec 1', held: true },
    { name: 'Gaming 2', shortName: 'Sec 2', held: true },
    { name: 'Gaming 3', shortName: 'Sec 2/3', held: true },
    { name: 'Gaming 4', shortName: 'Sec 3', held: true },
    { name: 'Gaming 5', shortName: 'Sec 4', held: true },
    { name: 'Gaming 6', shortName: 'Sec 5', held: true },
    { name: 'Service Corridor 1', shortName: 'BOH 1', held: true },
    { name: 'Conventions 1', shortName: 'Conv Rove', held: true },
    { name: 'Hotel 1', shortName: 'L&F', held: true },
  ]},
  { name: 'Tower', note: 'Hotel 4–6 held · Hotel 2, 3, 7, 8 rotate', color: '#7C3AED', posts: [
    { name: 'Hotel 2', shortName: 'Tower 1', held: false },
    { name: 'Hotel 3', shortName: 'Tower 2', held: false },
    { name: 'Hotel 4', shortName: 'Tower 3', held: true },
    { name: 'Hotel 5', shortName: 'Tower 4', held: true },
    { name: 'Hotel 6', shortName: 'Tower 5', held: true },
    { name: 'Hotel 7', shortName: 'Casino Elev', held: false },
    { name: 'Hotel 8', shortName: 'Prom Elev', held: false },
  ]},
  { name: 'Relief 1', note: 'Relief 1 gives breaks · entrances and docks rotate every 2 hours', color: '#0F766E', posts: [
    { name: 'Relief 1', shortName: 'Breaks', held: true },
    { name: 'Employee Entrance 1', shortName: '', held: false },
    { name: 'Employee Entrance 2', shortName: 'VIP', held: false },
    { name: 'Loading Docks 2', shortName: 'E. Dock', held: false },
    { name: 'Loading Docks 1', shortName: 'W. Dock', held: false },
    // 6th post (Employee Entrance 1, 2nd officer) added dynamically if ee1Two is true
  ]},
  { name: 'Pool', note: 'Pool Rove gives breaks · all held all shift', color: '#C2410C', posts: [
    { name: 'Pool Rove', shortName: 'Breaks', held: true },
    { name: 'Pool 1', shortName: 'Main Pool', held: true },
    { name: 'Pool 2 / Hotel 11', shortName: 'Prom Rove', held: true },
  ]},
  { name: 'Relief 2', note: 'Relief 2 gives breaks · cages, sky suites, valet rotate', color: '#BE185D', posts: [
    { name: 'Relief 2', shortName: 'Breaks', held: true },
    { name: 'Gaming 7', shortName: 'Main Cage 1', held: false },
    { name: 'Gaming 8', shortName: 'Main Cage 2', held: false },
    { name: 'Hotel 9', shortName: 'Sky Suite Elev', held: false },
    { name: 'North Valet', shortName: '', held: false },
  ]},
  { name: 'Report Writers', note: 'Security Office 2–4 · held all shift', color: '#1F1D3A', posts: [
    { name: 'Security Office 2', shortName: 'RW 1', held: true },
    { name: 'Security Office 3', shortName: 'RW 2', held: true },
    { name: 'Security Office 4', shortName: 'RW 3', held: true },
  ]},
];

/** Report Writers group index. */
export const RWG = 5;

/** Dispatch group index (virtual — not in GROUPS array, managed separately). */
export const DISP = 6;

/** Call-off priority order: [groupIndex, postIndex].
 *  P1 = Conventions 1 (OT), P2 = Svc Corridor 1 (OT), P3 = Hotel 5, P4 = Hotel 6, P5 = Gaming 3. */
export const PORDER: [number, number][] = [
  [0, 7],  // Conventions 1
  [0, 6],  // Service Corridor 1
  [1, 3],  // Hotel 5
  [1, 4],  // Hotel 6
  [0, 2],  // Gaming 3
];

export const PSHORT = ['Conventions 1', 'Svc Corridor 1', 'Hotel 5', 'Hotel 6', 'Gaming 3'];

/** Base swing roster — 32 officers, exact order from prototype line 1169. */
export const BASE_NAMES = [
  'Phisayavong, Steven', 'Washington, Yontay', 'Loadholt, Willie', 'Zamora, Santiago',
  'Russo, Fola', 'Higgins, John', 'Sar, Bona', 'Ward, John', 'Haws, Lori',
  'Peiser, Robert', 'Ziadeh, Issa', 'Maytorena, Emilio', 'Greco, Louis', 'Haley, Sean',
  'Martinez, Maria', 'Alexis, Melony', 'Gusman, Elizabeth', 'Fernandez, Andy',
  'Woods, Paul', 'Wheeler-Berry, Amanda', 'Young, Reginald', 'Navoa, Lucky',
  'Waller, LaDone', 'Burgueno, Marisol', 'Tipon, Wynnfred', 'Randle, Robert',
  'Cardenas, Julian', 'Dufour, Rina', 'Greene, Ira', 'Shepherd, Isaiah',
  'Marycz, Jacob', 'Fedai, Naweed',
];

/** Base report writers — indices into BASE_NAMES. */
export const BASE_RW_INDICES = [9, 10, 11]; // Peiser, Ziadeh, Maytorena

/** Base FTOs. */
export const BASE_FTO = ['Doroy, Mark', 'Szollose, Bradley', 'Greene, Ira', 'Hernandez, Victor'];

/** CCI dispatch seats. */
export const CCI_SEATS = [
  'Dispatch 1',
  'Dispatch 2 · Dispatch',
  'Dispatch 3 · Call Taker',
  'Dispatch 4 · Tower Dispatch',
];

/** CCI short labels. */
export const CCI_SHORT = ['Dispatch 1', 'Dispatch 2', 'Dispatch 3', 'Dispatch 4'];

/** Base CCI dispatchers. */
export const BASE_CCI = [
  'Wallace, Phillip', 'Takashima, Stacie', 'Servantes, Angel', 'Gonzalez-Miranda, Rafael',
];

/** Day shift roster. */
export const DAY_SHIFT_NAMES = [
  'McFarland, Grace', 'Ward, Sina', 'Delgado, Michael', 'William, Isaiah', 'Dye, Tony',
  'Eskridge, Michael', 'Madidi, Habib', 'Lozano, Vincent', 'Mathis, Jake', 'Romero, Jose',
  'Lowe, TaVon', 'Nguyen, Karine', 'Gonzalez Herrera, Marco', 'Parra, Jorge',
  'Marshall, Marcus', 'Carwell, Destin', 'Rexrode, Brandon', 'Salter, Zarr',
  'Turchetti, Valentin', 'Prince, Jimmie', 'Hernandez Sixtos, Azaheed', 'Miranda, Victor',
  'Gastelum, Jonathan', 'Lasalle, Daniel', 'Williams, Ricky', 'Lubic, Robert',
  'Rivera Aponte, Eduardo', 'Yee, Homer', 'Brecht, Ronald', 'Muro, Crystal',
  'Hale, Mya', 'King, Kelsey',
];

/** Grave shift roster. */
export const GRAVE_SHIFT_NAMES = [
  'Davenport, Michelle', 'Lesly, Despeigne', 'Albarran, Juan', 'Lule, Lidia',
  'Orduno, Riley', 'Santacruz, Iriscelly', 'Young, Eviee', 'Roseborough, Trenton',
  'Bailey, Sherman', 'Alejandre-Diaz, Gabino', 'Cain, Ashley', 'Asay, Jason',
  'Ballenger, Dustin', 'Luna, Tomas', 'Servin, Pierre', 'Douglas, Daion',
  'Acevedo, Alexander', 'Mapusua, Renei', 'Acuna, Javier', 'White, Anthony',
  'Reyes Guell, Emanuel', 'Leverton, Summer', 'Featherston, Adam', 'Mulcahy, Ryan',
  'Cortez, Meliton', 'Moran, Anthony', 'Williams, Mona', 'Hernandez, Daniel',
  'Collins, Reyna', 'Chavez, Jonathan', 'Irving, Ramon', 'Moody, Sulon', 'Perrotta, Orion',
];

/** Swing shift time blocks. */
export const BLOCKS = ['1400', '1600', '1800', '2000', '2200'];

/** Number of 2-hour blocks per shift. */
export const NUM_BLOCKS = 4;

/** Half-hour slot boundaries for swing (used by overtime). */
export const SW0 = 28; // 1400
export const SW1 = 44; // 2200

/** Color palette for officer badges. */
export const PALETTE = ['#B45309', '#15803D', '#B91C1C', '#1D4ED8', '#7C3AED', '#C2410C', '#0F766E', '#BE185D'];

/** Relief 1 rotation order when EE1 second officer is enabled. */
export const RELIEF1_ROT_WITH_EE1 = [1, 2, 5, 3, 4];
